<script setup lang="ts">
    import { Check, ChevronDown, ChevronRight, Minus } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
    } from '@/components/ui/dropdown-menu';
    import { Voucher } from '@/types';
    import type { Table } from '@tanstack/vue-table';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import { computed, ref } from 'vue';
    import BulkCloseVouchers from '@/components/vouchers/BulkCloseVouchers.vue';
    import BulkApproveVouchers from '@/components/vouchers/BulkApproveVouchers.vue';
    import BulkRemoveVouchers from '@/components/vouchers/BulkRemoveVouchers.vue';
    import BulkIgnoreVouchers from '@/components/vouchers/BulkIgnoreVouchers.vue';
    import BulkMarkAsMissingVouchers from '@/components/vouchers/BulkMarkAsMissingVouchers.vue';
    import BulkAssignAssignedVouchers from '@/components/vouchers/BulkAssignAssignedVouchers.vue';
    import BulkAssignProjectVouchers from '@/components/vouchers/BulkAssignProjectVouchers.vue';
    import BulkCloseVouchersAlertDialog from '@/components/vouchers/BulkCloseVouchersAlertDialog.vue';
    import BulkApproveVouchersAlertDialog from '@/components/vouchers/BulkApproveVouchersAlertDialog.vue';
    import BulkRemoveVouchersAlertDialog from '@/components/vouchers/BulkRemoveVouchersAlertDialog.vue';
    import BulkIgnoreVouchersAlertDialog from '@/components/vouchers/BulkIgnoreVouchersAlertDialog.vue';
    import BulkMarkAsMissingVouchersDialog from '@/components/vouchers/BulkMarkAsMissingVouchersDialog.vue';
    import BulkAssignAssignedVouchersDialog from '@/components/vouchers/BulkAssignAssignedVouchersDialog.vue';
    import BulkAssignProjectVouchersDialog from '@/components/vouchers/BulkAssignProjectVouchersDialog.vue';

    const props = defineProps<{
        table: Table<Voucher>;
    }>();

    const { hasPermission } = usePermissions();

    const selectedVouchers = computed<Voucher[]>(() =>
        props.table.getSelectedRowModel().rows.map((row) => row.original),
    );
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <Button
                variant="ghost_primary"
                size="xs"
                width="narrow"
                class="ml-auto"
                :disabled="!selectedVouchers.length"
            >
                {{ $t('vouchers.select.other_options') }}
                <ChevronDown />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
            <BulkCloseVouchers :selected-vouchers="selectedVouchers" />
            <BulkApproveVouchers :selected-vouchers="selectedVouchers" />
            <BulkRemoveVouchers
                v-if="hasPermission(Permission.VOUCHERS_ADMIN_DELETE)"
                :selected-vouchers="selectedVouchers"
            />
            <BulkIgnoreVouchers :selected-vouchers="selectedVouchers" />
            <BulkMarkAsMissingVouchers :selected-vouchers="selectedVouchers" />
            <BulkAssignAssignedVouchers :selected-vouchers="selectedVouchers" />
            <BulkAssignProjectVouchers :selected-vouchers="selectedVouchers" />
        </DropdownMenuContent>
    </DropdownMenu>

    <BulkCloseVouchersAlertDialog :table="table" />
    <BulkApproveVouchersAlertDialog :table="table" />
    <BulkRemoveVouchersAlertDialog :table="table" />
    <BulkIgnoreVouchersAlertDialog :table="table" />
    <BulkMarkAsMissingVouchersDialog :table="table" />
    <BulkAssignAssignedVouchersDialog :table="table" />
    <BulkAssignProjectVouchersDialog :table="table" />
</template>
